/**
 * Live tool calls and tool results in the chat message list (agion/Agion#2003).
 *
 * A frame delivered again shows once. A frame repeats a message in the list
 * only when both have the same non-empty tool call id and the same content:
 * for a result its success, output, error, output files and duration; for a
 * call its tool name and input. Tool call ids are not always unique (a model
 * that issues none gets a made-up one), so a frame that reuses an id with other
 * content is a different call or result. It is kept under the next free
 * message id (base, base-2, base-3, ...), so React keys stay unique. A frame
 * without a tool call id cannot be matched to another one and is always kept.
 */

import type { StreamingChatMessage } from '../hooks/useStreamingMissionChat'

type MessageData = StreamingChatMessage['data']

/** What a tool call message shows: its tool name and input. */
export function toolCallContent(data: MessageData): string {
  return JSON.stringify([data.toolName, data.toolInput])
}

/** What a tool result message shows: its success, output, error, output files and duration. */
export function toolResultContent(data: MessageData): string {
  return JSON.stringify([data.success, data.output, data.error, data.outputFiles, data.durationMs])
}

/**
 * The list with the tool message added, or the same list when the message
 * repeats one of the same type in it. message.id is the base id; the added
 * message gets the base id or the next free one.
 */
export function withLiveToolMessage(
  messages: StreamingChatMessage[],
  message: StreamingChatMessage,
  content: (data: MessageData) => string,
): StreamingChatMessage[] {
  const toolCallId = message.data.toolCallId
  if (toolCallId) {
    const shown = content(message.data)
    const repeats = messages.some(
      (m) => m.type === message.type && m.data.toolCallId === toolCallId && content(m.data) === shown,
    )
    if (repeats) return messages
  }
  return [...messages, { ...message, id: freeMessageId(messages, message.id) }]
}

function freeMessageId(messages: readonly StreamingChatMessage[], base: string): string {
  const taken = new Set(messages.map((m) => m.id))
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}
